import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { cloneDeep, isEqual } from 'lodash';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';
import {
  BulkLineItemColumns,
  initialBulkLineItemData,
} from 'src/assets/static/invoice_details/BulkAddLineItemColumns';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import GridWrapper from 'src/components/GridWrapper';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { MIN_LINE_ITEMS } from 'src/utils/constants';
import { useContextSelector } from 'use-context-selector';
import useBulkLineItemErrors, {
  BulkLineItemError,
  BulkLineItemInfo,
} from './errors/useBulkLineItemErrors';
import { InvoiceContext } from './InvoiceDetailsContext';
import useInvoiceButtonState from './useInvoiceButtonState';

export interface BulkLineItem {
  rowId: string;
  index: number;
  selected: boolean;
  field: string;
  title: string;
  value: any;
  appendLineItem: boolean;
  showPreview: boolean;
  errors?: string[];
  infos?: string[];
  warnings?: string[];
}

const BulkAddLineItems = () => {
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const numLineItems = useContextSelector(InvoiceContext, (context) => context.numLineItems);
  const setNumLineItems = useContextSelector(InvoiceContext, (context) => context.setNumLineItems);
  const [data, setData] = useState(cloneDeep(initialBulkLineItemData));
  const inputRef = useRef<HTMLInputElement>(null);
  const { invoiceLineItems, setInvoiceLineItems } = useContextSelector(
    InvoiceContext,
    (context) => ({
      invoiceLineItems: context.invoiceLineItems,
      setInvoiceLineItems: context.setInvoiceLineItems,
    })
  );

  const { errors, infos, errorList } = useBulkLineItemErrors(data, undefined, 'add');

  // reset to default check
  const hasDataChanged = useMemo(() => !isEqual(data, initialBulkLineItemData), [data]);

  const isBulkAddDirty = hasDataChanged || numLineItems !== MIN_LINE_ITEMS;

  const confirmBeforeProceeding = useCallback(
    (acceptCallback: VoidFunction) => {
      if (isBulkAddDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. To continue editing, please go back (Cancel). If you proceed, your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
          }
        });
      } else {
        acceptCallback();
      }
    },
    [confirm, isBulkAddDirty]
  );

  const validatedData: BulkLineItem[] = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        errors: errors[index][item.field as keyof BulkLineItemError] || [],
        infos: infos[index][item.field as keyof BulkLineItemInfo] || [],
      })),
    [data, errors, infos]
  );

  const handleHeaderSelectionChange = (e: GridHeaderSelectionChangeEvent) => {
    const { checked }: any = e.syntheticEvent.target;
    setData((prev) =>
      prev.map((item) => ({
        ...item,
        selected: checked,
      }))
    );
  };

  const handleSelectionChange = useCallback((e: GridSelectionChangeEvent) => {
    setData((prev) => {
      const prevItems = [...prev];
      const index = e?.startRowIndex;
      prevItems[index].selected = !prevItems[index].selected;
      return prevItems;
    });
  }, []);

  const openBulkAddModal = () => {
    setOpenModal(true);
    setData(cloneDeep(initialBulkLineItemData));
    setNumLineItems(MIN_LINE_ITEMS);
  };

  const closeBulkAddModal = () => {
    confirmBeforeProceeding(() => {
      setOpenModal(false);
    });
  };

  const handleItemChange = (e: GridItemChangeEvent) => {
    const { dataItem, value, field } = e;
    setData((prev) => {
      const prevItems = [...prev];
      const index = dataItem?.index as number;
      prevItems[index][field as keyof BulkLineItem] = value as never;
      return prevItems;
    });
  };

  const handleIncrement = () => setNumLineItems((n: number) => (n < 50 ? n + 1 : 50));
  const handleDecrement = () => setNumLineItems((n: number) => (n > 1 ? n - 1 : 1));

  const handleNumLineItemsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const num = Number(val);
    if (!isNaN(num) && val !== '') {
      if (num < 1) setNumLineItems(1);
      else if (num > 50) setNumLineItems(50);
      else setNumLineItems(num);
    }
  };

  const handleInputFocus = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.select();
    }
  }, []);

  const handleBulkAddLineItems = () => {
    const itemNumber = data.find((item) => item.field === 'productItemNum');
    const description = data.find((item) => item.field === 'description');
    const glCd = data.find((item) => item.field === 'glCd');
    const priceAmt = data.find((item) => item.field === 'priceAmt');
    const uom = data.find((item) => item.field === 'uom');
    const quantityReceived = data.find((item) => item.field === 'quantityReceived');
    const taxable = data.find((item) => item.field === 'isTaxable');
    const newLineItems: InvoiceLineItem[] = [];
    let currentSequenceNumber = invoiceLineItems.length + 1;
    for (let i = 0; i < numLineItems; i++) {
      newLineItems.push({
        canDelete: 0,
        isTaxable: taxable?.selected ? taxable.value : true,
        description: description?.selected
          ? description.value + ` ${description.appendLineItem ? currentSequenceNumber : ''}`
          : '',
        glCd: glCd?.selected ? glCd.value : '',
        poQty: 0,
        quantityReceived: quantityReceived?.selected ? quantityReceived.value : 0,
        priceAmt: priceAmt?.selected ? priceAmt.value : 0,
        uom: uom?.selected ? uom.value : '',
        rowId: uuid(),
        subTotal:
          priceAmt?.selected && quantityReceived?.selected
            ? priceAmt.value * quantityReceived.value
            : 0,
        productItemNum: itemNumber?.selected
          ? itemNumber.value + ` ${itemNumber.appendLineItem ? currentSequenceNumber : ''}`
          : '',
        isNewLineItem: true,
        isNewlyAddedLineItemNow: true,
        completed: 'N',
        receiptLineItemId: Number(`${parseInt(String(Math.random() * 100000))}987654321`),
      } as unknown as InvoiceLineItem);
      currentSequenceNumber++;
    }
    setInvoiceLineItems((prev) => [...prev, ...newLineItems]);
    setOpenModal(false);
  };

  const [, buttonState] = useInvoiceButtonState('AddLineItem');
  return (
    <div>
      <Button onClick={openBulkAddModal} buttonState={buttonState}>
        Bulk Add Line Item(s)
      </Button>
      <Dialog
        width="800px"
        id="modal"
        title={'Bulk Add Line Item(s)'}
        open={openModal}
        onClose={closeBulkAddModal}
      >
        <div className="flex items-center gap-2 mb-4 mt-2 text-sm">
          <span>Add</span>
          <div className="flex items-center ">
            <Button
              onClick={handleDecrement}
              color="secondary"
              className="rounded-l m-0"
              size="md"
              disabled={numLineItems <= 1}
            >
              -
            </Button>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              value={numLineItems}
              onChange={handleNumLineItemsChange}
              onFocus={handleInputFocus}
              className="w-11 text-center border p-[4px] text-sm"
            />
            <Button
              onClick={handleIncrement}
              color="secondary"
              className="rounded-r m-0"
              size="md"
              disabled={numLineItems >= 50}
            >
              +
            </Button>
          </div>
          <span>new line item(s)</span>
        </div>
        <div className="mb-4 text-sm">
          <span>
            The checked values below will be applied to all new line item(s) in their respective
            columns. Uncheck a checkbox to add line item(s) to the document with no value (or the
            system default value) instead.
          </span>
        </div>
        <div>
          <GridWrapper
            id="BulkAddTable"
            DataItemKey="rowId"
            className={'auto-scrollbar'}
            hideScrollBar
            columns={BulkLineItemColumns}
            dataSource={validatedData}
            onSelectionChange={handleSelectionChange}
            onHeaderSelectionChange={handleHeaderSelectionChange}
            onItemChange={handleItemChange}
            gridToolbarChildren={
              <BulkAddLineItemsToolbar
                onReset={() => setData(cloneDeep(initialBulkLineItemData))}
                disabled={!hasDataChanged}
              />
            }
            resizable={true}
          />
        </div>
        <DialogActionsBar>
          <div className="flex justify-end">
            <PopUp
              content={
                <div className="m-1 max-w-[300px]">
                  <PopUpContentWrapper type="Error" contents={errorList} />
                </div>
              }
              show={errorList.length > 0}
            >
              <Button disabled={errorList.length > 0} onClick={handleBulkAddLineItems} color="info">
                {t('Add')}
              </Button>
            </PopUp>
            <Button onClick={closeBulkAddModal} color="info">
              {t('Cancel')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
};

interface BulkAddLineItemsToolbarProps {
  onReset: VoidFunction;
  disabled?: boolean;
}

const BulkAddLineItemsToolbar = memo(
  ({ onReset, disabled = false }: BulkAddLineItemsToolbarProps) => (
    <div className="flex items-center justify-end w-full">
      <Button color="info" onClick={onReset} size="md" disabled={disabled}>
        Reset to Defaults
      </Button>
    </div>
  )
);

export default BulkAddLineItems;
